import { useRef, useState } from 'react';
import { Object3D } from 'three';

import { ThreeEvent } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';

export interface SelectPointerHookOptions {
  /**
   * Whether to enable the select box or not
   */
  enabled?: boolean;
  /**
   * Whether to propagate the click event or not
   */
  propagationClick?: boolean;
}

export type SelectAction = {
  object?: Object3D;
  shift: boolean;
  holdTime: number;
};

/**
 * Find the first Instance object under the pointer
 * If no Instance found, fallback to first intersection object
 */
const captureObjectUnderPointer = (event: ThreeEvent<PointerEvent>) => {
  const instance = event.intersections.find((o) => (o.object as any).isInstance);
  return instance?.object ?? event.object;
};

export const useSelectPointer = (
  dispatch: React.Dispatch<SelectAction>,
  opts?: SelectPointerHookOptions
) => {
  const { enabled = true, propagationClick } = { ...opts };

  const [hovered, hover] = useState(false);
  const timer = useRef<number>(0);

  const onPointerDown = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    if (!enabled) return;
    if (!propagationClick) {
      evt.stopPropagation();
    }
    timer.current = Date.now();
  });

  const onPointerUp = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    if (!enabled) return;
    if (!propagationClick) {
      evt.stopPropagation();
    }
    dispatch({
      object: captureObjectUnderPointer(evt),
      shift: evt.shiftKey,
      holdTime: timer.current ? Date.now() - timer.current : 0,
    });
    timer.current = 0;
  });

  const onPointerOver = useEventCallback(() => {
    if (!enabled) return;
    hover(true);
  });

  const onPointerOut = useEventCallback(() => {
    if (!enabled) return;
    hover(false);
  });

  const onPointerMissed = useEventCallback(() => {
    if (!enabled) return;
    if (!hovered) {
      dispatch({
        shift: false,
        holdTime: timer.current ? Date.now() - timer.current : 0,
      });
      timer.current = 0;
    }
  });

  return {
    onPointerDown,
    onPointerUp,
    onPointerOver,
    onPointerOut,
    onPointerMissed,
  };
};
